import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { accuracyPath, agentDetailPath, agentLogsPath, useApi } from "../api/client";
import type {
  AccuracyReport,
  AgentDetailResponse,
  AgentLogResponse,
  Guardrail,
} from "../api/types";
import { AgentFields } from "../components/AgentFields";
import {
  IconTile,
  LogStatus,
  StateSwitch,
  StatusPill,
} from "../components/AgentBits";
import { BarChart, LineChart, Meter, seriesColor } from "../components/Charts";
import { CostCell } from "../components/CostCell";
import { Pagination } from "../components/Pagination";
import { AgentAccuracy } from "../components/AgentAccuracy";
import { AgentFormats } from "../components/AgentFormats";
import { ScoredFields } from "../components/ScoredFields";
import { Empty, ErrorState, Loading } from "../components/States";
import {
  SubmitterFilter,
  useSubmittedBy,
  withSubmitter,
} from "../components/SubmitterFilter";
import { Breadcrumb, Toolbar } from "../components/Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import {
  EM_DASH,
  fmtCost,
  fmtDateTime,
  fmtMs,
  fmtTokens,
  humanise,
} from "../lib/format";

type Tab =
  | "guardrails"
  | "model"
  | "performance"
  | "fields"
  | "formats"
  | "usage"
  | "logs";

//: Tab order, as specified. Configuration first, then what the agent did:
//: Guardrails and Model describe how it is set up, Performance and Fields what
//: it produced, Usage what that cost, Logs the individual events.
const TABS: { key: Tab; label: string }[] = [
  { key: "guardrails", label: "Guardrails" },
  { key: "model", label: "Model" },
  { key: "performance", label: "Performance" },
  { key: "fields", label: "Fields" },
  // After Fields, because a format is only scoreable once the fields it is
  // scored on have been chosen.
  { key: "formats", label: "Formats" },
  { key: "usage", label: "Usage" },
  { key: "logs", label: "Logs" },
];

const dayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

export function AgentDetail() {
  const { type = "" } = useParams();
  const navigate = useNavigate();
  // The tab lives in the URL so it can be linked to. A document page sends a
  // reader straight here to change which fields are scored, and landing them on
  // Guardrails with a "now find the Fields tab" implied is a worse answer than
  // the link not existing.
  const [tabParams, setTabParams] = useSearchParams();
  const requested = tabParams.get("tab") as Tab | null;
  const tab: Tab = TABS.some((t) => t.key === requested)
    ? (requested as Tab)
    : TABS[0].key;
  const setTab = (next: Tab) => {
    const p = new URLSearchParams(tabParams);
    p.set("tab", next);
    // replace: flipping between tabs should not fill the back button with
    // steps the reader has to press through to leave the page.
    setTabParams(p, { replace: true });
  };
  // Narrows the KPIs, charts and field stats. Not the measured-accuracy tile,
  // the router facts, or the Logs/Fields/Formats tabs, which stay agent-wide.
  const [submittedBy, setSubmittedBy] = useSubmittedBy();
  const { data, error, loading, reload } = useApi<AgentDetailResponse>(
    type ? agentDetailPath(type, 30, submittedBy || undefined) : null,
  );
  // Its own request rather than a field on the agent response: scoring walks
  // every verified field, and an agent page should render its measured
  // metrics without waiting on it.
  const { data: accuracy } = useApi<AccuracyReport>(type ? accuracyPath(type) : null);

  if (loading)
    return (
      <div className="card">
        <Loading what="agent" />
      </div>
    );
  if (error)
    return (
      <div className="card">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  if (!data) return null;

  const k = data.kpis;

  return (
    <>
      <Breadcrumb
        trail={[
          { label: "All agents", to: withSubmitter("/agents", submittedBy) },
          { label: data.label },
        ]}
      />

      <div className="range-group" style={{ justifyContent: "flex-end", marginBottom: 12 }}>
        <SubmitterFilter value={submittedBy} onChange={setSubmittedBy} idPrefix="agent" />
      </div>

      <div className="tiles">
        <div className="tile">
          <div className="tile-label">Avg confidence</div>
          <div className="tile-value">
            {k.avg_confidence === null
              ? EM_DASH
              : `${k.avg_confidence.toFixed(1)}%`}
          </div>
          <div className="tile-note">self-reported, not accuracy</div>
        </div>
        {/* Sits next to avg confidence on purpose: they answer different
            questions about the same agent. Confidence is what the model
            thought; this is whether it produced the fields it is required to
            produce, which is checked rather than reported. */}
        <div className="tile">
          <div className="tile-label">Required fields</div>
          <div className="tile-value">
            {!data.requirements.defined
              ? EM_DASH
              : data.requirements.coverage.score === null
                ? EM_DASH
                : `${data.requirements.coverage.score}%`}
          </div>
          <div className="tile-note">
            {!data.requirements.defined
              ? "none defined yet"
              : data.requirements.coverage.runs === 0
                ? "no extractions to score"
                : `${data.requirements.coverage.complete} of ${data.requirements.coverage.runs} runs complete`}
          </div>
        </div>
        {/* The only measured number on this page. Everything beside it is the
            model's self-report or a policy applied to one. */}
        <div className="tile">
          <div className="tile-label">Measured accuracy</div>
          <div className="tile-value">
            {!accuracy || accuracy.scored === 0
              ? EM_DASH
              : `${((accuracy.metrics.accuracy ?? 0) * 100).toFixed(1)}%`}
          </div>
          <div
            className={`tile-note${accuracy && (accuracy.totals.hallucinated ?? 0) > 0 ? " warn" : ""}`}
          >
            {!accuracy || accuracy.scored === 0
              ? "nothing verified yet"
              : (accuracy.totals.hallucinated ?? 0) > 0
                ? `${accuracy.totals.hallucinated} invented value(s) found`
                : `${accuracy.verified_documents} document(s) verified`}
          </div>
        </div>
        <div className="tile">
          <div className="tile-label">Extractions</div>
          <div className="tile-value">{fmtTokens(k.runs)}</div>
          <div className={`tile-note${k.failures ? " warn" : ""}`}>
            {k.failures ? `${k.failures} failed` : "all completed"}
          </div>
        </div>
        <div className="tile">
          <div className="tile-label">Latency</div>
          <div className="tile-value">{fmtMs(k.p50_latency_ms)}</div>
          <div className="tile-note">p50 · p95 {fmtMs(k.p95_latency_ms)}</div>
        </div>
        <div className="tile">
          <div className="tile-label">Cost</div>
          <div className="tile-value">{fmtCost(k.cost_usd)}</div>
          <div className={`tile-note${k.unpriced_calls ? " warn" : ""}`}>
            {k.unpriced_calls
              ? `${k.unpriced_calls} call(s) with no rate — lower bound`
              : `${fmtTokens(k.total_tokens)} tokens`}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="agent-head">
          <IconTile initials={data.initials} accent={data.accent} />
          <div className="agent-head-main">
            <div className="agent-head-title">
              <StatusPill status={data.status} />
              <h1>{data.label}</h1>
            </div>
            <p className="agent-head-desc">{data.description}</p>
            <div className="chips">
              {data.tags.map((t) => (
                <span className="chip" key={t}>
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="head-actions">
            <button
              onClick={() =>
                navigate(
                  withSubmitter(
                    `/documents?document_type=${encodeURIComponent(data.document_type)}`,
                    submittedBy,
                  ),
                )
              }
            >
              View documents
            </button>
            <button
              className="btn-primary"
              disabled
              title="Submit a document through POST /api/orchestrator/documents/submit. There is no synthetic test-run path, and inventing one would produce results that are not extractions."
            >
              ▶ Test run
            </button>
          </div>
        </div>

        <div className="tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              className="tab"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 24px to match the card-to-card rhythm in the stylesheet. */}
      <div style={{ marginTop: 24 }}>
        {tab === "fields" ? (
          <>
            {/* Above the evidence table on purpose: the switches are a decision
                and the fill rates below are what informs it, so the order is
                decide-then-check rather than scroll-back-up. */}
            <div style={{ marginBottom: 24 }}>
              <ScoredFields documentType={data.document_type} />
            </div>
            <AgentFields data={data} />
            {accuracy ? (
              <div style={{ marginTop: 24 }}>
                <AgentAccuracy report={accuracy} documentType={data.document_type} />
              </div>
            ) : null}
          </>
        ) : null}
        {tab === "formats" ? (
          <AgentFormats documentType={data.document_type} />
        ) : null}
        {tab === "guardrails" ? (
          <GuardrailsTab rails={data.guardrails} />
        ) : null}
        {tab === "model" ? <ModelTab data={data} /> : null}
        {tab === "performance" ? <PerformanceTab data={data} /> : null}
        {tab === "usage" ? <UsageTab data={data} /> : null}
        {tab === "logs" ? <LogsTab type={data.document_type} /> : null}
      </div>
    </>
  );
}

/* ----------------------------------------------------------- guardrails --- */

function GuardrailsTab({ rails }: { rails: Guardrail[] }) {
  const problems = rails.filter((r) => r.severity !== "good");

  return (
    <>
      <div className="page-head" style={{ marginBottom: 12 }}>
        <div className="section-title" style={{ margin: 0 }}>
          Active guardrails <span className="pill">{rails.length}</span>
        </div>
        <div className="head-actions">
          <button
            disabled
            title="Guardrails are configuration, not data. Each row names the file or environment variable that owns its value."
          >
            + Add rule
          </button>
        </div>
      </div>

      {problems.length ? (
        <div className="notice notice-warn">
          <strong>
            {problems.length} guardrail{problems.length === 1 ? "" : "s"} not
            fully protective.
          </strong>{" "}
          {problems.map((p) => p.title).join(", ")}.
        </div>
      ) : null}

      {rails.map((r) => (
        <div className="rail" key={r.key}>
          <span
            className="icon-tile"
            style={{
              background: `color-mix(in srgb, var(--${r.severity === "good" ? "s1" : r.severity}) 14%, transparent)`,
              color: `var(--${r.severity === "good" ? "s1" : r.severity})`,
            }}
            aria-hidden="true"
          >
            {r.initials}
          </span>

          <div className="rail-main">
            <div className="rail-title">
              {r.title}
              <span
                className={`pill pill-${r.severity === "good" ? "good" : r.severity}`}
              >
                {r.state}
              </span>
            </div>
            <div className="rail-desc">{r.description}</div>
            <div className="rail-detail">{r.detail}</div>
            <div className="rail-source">{r.source}</div>
          </div>

          <StateSwitch on={r.enabled} label={r.title} />
        </div>
      ))}

      <div className="notice" style={{ marginTop: 16, marginBottom: 0 }}>
        This panel is <strong>read-only</strong>. Every value above is read from
        the live configuration, so it is accurate — but nothing here writes it.
        Change a guardrail in the file or environment variable named under it,
        then restart that service. A switch that looked operable while changing
        nothing would be worse than no switch.
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- model --- */

function ModelTab({ data }: { data: AgentDetailResponse }) {
  const m = data.model;
  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Extraction model</span>
        </div>
        <div className="card-body">
          <dl className="kv">
            <dt>Model</dt>
            <dd className="mono">{m.model ?? EM_DASH}</dd>
            <dt>Prompt version</dt>
            <dd className="mono">{m.prompt_version ?? EM_DASH}</dd>
            <dt>Jurisdiction</dt>
            <dd>{m.jurisdiction ?? EM_DASH}</dd>
            <dt>Knowledge pack</dt>
            <dd className="mono">{m.knowledge_pack_version ?? EM_DASH}</dd>
            <dt>First run</dt>
            <dd>{fmtDateTime(data.kpis.first_run_at)}</dd>
            <dt>Last run</dt>
            <dd>{fmtDateTime(data.kpis.last_run_at)}</dd>
          </dl>
        </div>
      </div>

      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Routing</span>
        </div>
        <div className="card-body">
          {m.router_decisions ? (
            <dl className="kv">
              <dt>Tier</dt>
              <dd className="mono">{m.router_tier ?? EM_DASH}</dd>
              <dt>Provider</dt>
              <dd className="mono">{m.router_provider ?? EM_DASH}</dd>
              <dt>Executed model</dt>
              <dd className="mono">{m.router_executed_model ?? EM_DASH}</dd>
              <dt>Reason</dt>
              <dd>{m.router_reason ?? EM_DASH}</dd>
              <dt>Decisions</dt>
              <dd>{fmtTokens(m.router_decisions)}</dd>
              <dt>Cache hits</dt>
              <dd>{fmtTokens(m.router_cache_hits)}</dd>
              <dt>Failovers</dt>
              <dd>
                {m.router_failovers ? (
                  <span className="pill pill-warning">
                    {m.router_failovers}
                  </span>
                ) : (
                  "0"
                )}
              </dd>
            </dl>
          ) : (
            <Empty
              title="No routing decisions recorded"
              hint="Extractions for this type ran without consulting the router, or the router was unreachable."
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- performance --- */

function PerformanceTab({ data }: { data: AgentDetailResponse }) {
  const p = data.performance;
  const navigate = useNavigate();
  // The bars count one submitter's documents when the page is filtered, so the
  // list a bar opens has to be filtered the same way.
  const [submittedBy] = useSubmittedBy();

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Call volume</span>
          <span className="muted">last {data.days} days</span>
        </div>
        <div className="card-body">
          <BarChart
            data={p.volume.map((v) => ({
              label: dayLabel(v.day),
              sub: new Date(v.day).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }),
              value: v.runs,
            }))}
            valueLabel="Extractions"
          />
        </div>
      </div>

      {/* The review entry point: which extractions deserve a human.
          Bars are buttons, because a distribution nobody can open is a picture
          of a problem rather than a way into it. Each one lands on the document
          list already filtered to that band, where the existing row click opens
          the full extraction beside its source PDF. */}
      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Confidence distribution</span>
          <span className="muted">click a band to review those documents</span>
        </div>
        <div className="card-body">
          {p.confidence_bands.every((b) => b.runs === 0) ? (
            <Empty
              title="Nothing extracted yet"
              hint="This agent has no runs in the window."
            />
          ) : (
            <>
              {p.confidence_bands.map((b) => {
                const max = Math.max(
                  ...p.confidence_bands.map((x) => x.runs),
                  1,
                );
                return (
                  <button
                    key={b.band}
                    className="band-row"
                    disabled={b.runs === 0}
                    title={
                      b.band === "unscored"
                        ? "No confidence recorded — these came from the regex fallback, not an agent"
                        : `Review the ${b.runs} document(s) scored ${b.band}%`
                    }
                    onClick={() =>
                      navigate(
                        withSubmitter(
                          `/documents?document_type=${encodeURIComponent(data.document_type)}` +
                            `&confidence_band=${encodeURIComponent(b.band)}`,
                          submittedBy,
                        ),
                      )
                    }
                  >
                    <span className="band-label">
                      {b.band === "unscored" ? "unscored" : `${b.band}%`}
                    </span>
                    <span className="band-track">
                      <span
                        className="band-fill"
                        style={{
                          width: `${(b.runs / max) * 100}%`,
                          background:
                            b.band === "unscored"
                              ? "var(--ink-muted)"
                              : b.min !== null && b.min >= 85
                                ? "var(--good)"
                                : b.min !== null && b.min >= 70
                                  ? "var(--warning)"
                                  : "var(--critical)",
                        }}
                      />
                    </span>
                    <span className="band-count">{b.runs}</span>
                  </button>
                );
              })}
              <p className="muted" style={{ fontSize: 12, margin: "10px 0 0" }}>
                Lowest bands first when checking extractions — that is where a
                correction teaches the most.
              </p>
            </>
          )}
        </div>
      </div>

      {/* The same `volume` rows the bar chart above uses already carry an
          avg_confidence per day, and nothing drew it -- so the page could say
          what this agent's confidence is today and not whether it is falling.
          A trend is the question worth asking of a quality number. */}
      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Confidence trend</span>
          <span className="muted">last {data.days} days</span>
        </div>
        <div className="card-body">
          {p.volume.some((v) => v.avg_confidence !== null) ? (
            <>
              <LineChart
                labels={p.volume.map((v) => dayLabel(v.day))}
                series={[
                  {
                    name: "Avg confidence %",
                    values: p.volume.map((v) =>
                      v.avg_confidence === null ? 0 : Number(v.avg_confidence),
                    ),
                  },
                ]}
              />
              <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                Self-reported by the model, averaged per day. Not accuracy —
                that would need labelled ground truth, which only the golden
                records provide.
              </p>
            </>
          ) : (
            <Empty
              title="No confidence recorded"
              hint="These extractions came from the regex fallback rather than an agent, and a regex reports no confidence."
            />
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Confidence gate outcomes</span>
        </div>
        <div className="card-body">
          {p.gate_total === 0 ? (
            <Empty
              title="No gate decisions recorded"
              hint="The confidence gate writes its result into the extraction; none of these documents carries one."
            />
          ) : (
            <>
              {p.gate_outcomes.map((g) => (
                <Meter
                  key={g.action}
                  label={humanise(g.action)}
                  value={g.share ?? 0}
                  display={`${g.count} · ${g.share?.toFixed(1) ?? EM_DASH}%`}
                  color={
                    g.action === "AUTO_ROUTE"
                      ? "var(--good)"
                      : g.action === "REVIEW_WARNING"
                        ? "var(--warning)"
                        : g.action === "MANUAL_REVIEW"
                          ? "var(--serious)"
                          : "var(--s1)"
                  }
                />
              ))}
              <div
                className="notice"
                style={{ marginTop: 20, marginBottom: 0 }}
              >
                Thresholds: <strong>AUTO_ROUTE</strong> at 85% or above ·{" "}
                <strong>REVIEW_WARNING</strong> 70–84% ·{" "}
                <strong>MANUAL_REVIEW</strong> below 70%. Scored from 40%
                document quality and 60% deterministic field checks.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- usage --- */

function UsageTab({ data }: { data: AgentDetailResponse }) {
  const u = data.usage;
  const totalCost = u.stages.reduce((s, x) => s + (x.cost_usd ?? 0), 0);
  const anyPriced = u.stages.some((x) => x.cost_usd !== null);

  return (
    <>
      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Token usage by day</span>
          <span className="muted">last {data.days} days</span>
        </div>
        <div className="card-body">
          <LineChart
            labels={u.tokens_by_day.map((d) => dayLabel(d.day))}
            series={[
              {
                name: "Input tokens",
                values: u.tokens_by_day.map((d) => d.input_tokens),
              },
              {
                name: "Output tokens",
                values: u.tokens_by_day.map((d) => d.output_tokens),
              },
            ]}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-head bordered">
          <span className="card-title">Cost breakdown by stage</span>
          <span className="muted">
            {fmtTokens(u.input_tokens)} in · {fmtTokens(u.output_tokens)} out
          </span>
        </div>
        <div className="card-body">
          {u.stages.length === 0 ? (
            <Empty
              title="No usage recorded for this agent"
              hint="Nothing was logged. That is not the same as processing being free."
            />
          ) : (
            <>
              {u.stages.map((s, i) => {
                const free = s.cost_verified && s.total_tokens === 0;
                return (
                  <Meter
                    key={`${s.stage}-${s.model ?? i}`}
                    label={`${humanise(s.stage)} — ${s.provider ?? EM_DASH}/${s.model ?? EM_DASH}`}
                    value={
                      s.cost_usd !== null && totalCost > 0
                        ? (s.cost_usd / totalCost) * 100
                        : 0
                    }
                    display={
                      free
                        ? "$0.00 free"
                        : s.cost_usd === null
                          ? `${EM_DASH} no rate`
                          : fmtCost(s.cost_usd)
                    }
                    color={seriesColor(i)}
                  />
                );
              })}

              <div className="table-wrap" style={{ marginTop: 24 }}>
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Stage</th>
                      <th scope="col" className="num">
                        Calls
                      </th>
                      <th scope="col" className="num">
                        Tokens
                      </th>
                      <th scope="col" className="num">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {u.stages.map((s, i) => (
                      <tr key={`r-${s.stage}-${s.model ?? i}`}>
                        <td>
                          {humanise(s.stage)}
                          <div className="mono muted">
                            {s.provider ?? EM_DASH}/{s.model ?? EM_DASH}
                          </div>
                        </td>
                        <td className="num">{fmtTokens(s.calls)}</td>
                        <td className="num">{fmtTokens(s.total_tokens)}</td>
                        <td className="num">
                          <CostCell
                            usd={s.cost_usd}
                            verifiedFree={
                              s.cost_verified && s.total_tokens === 0
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row">Total</th>
                      <td className="num">
                        <strong>
                          {fmtTokens(u.stages.reduce((s, x) => s + x.calls, 0))}
                        </strong>
                      </td>
                      <td className="num">
                        <strong>
                          {fmtTokens(
                            u.stages.reduce((s, x) => s + x.total_tokens, 0),
                          )}
                        </strong>
                      </td>
                      <td className="num">
                        <strong>
                          {anyPriced ? fmtCost(totalCost) : EM_DASH}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ----------------------------------------------------------------- logs --- */

const LOG_KINDS = [
  { key: "execution", label: "Execution logs" },
  { key: "decision", label: "Decision logs" },
  { key: "config", label: "Config audit" },
];

function LogsTab({ type }: { type: string }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const page = Number(params.get("log_page") ?? 1);
  const active = (params.get("log_types") ?? "execution,decision,config")
    .split(",")
    .filter(Boolean);
  const [q, setQ] = useState(params.get("log_q") ?? "");

  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (q) next.set("log_q", q);
      else next.delete("log_q");
      if (next.get("log_q") !== params.get("log_q")) {
        next.set("log_page", "1");
        setParams(next, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const toggleKind = (key: string) => {
    const next = new URLSearchParams(params);
    const set = new Set(active);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    // Never leave every filter off -- an empty result set reads as "no logs
    // exist", which would be misleading.
    if (set.size === 0) return;
    next.set("log_types", [...set].join(","));
    next.set("log_page", "1");
    setParams(next);
  };

  const { data, error, loading, reload } = useApi<AgentLogResponse>(
    agentLogsPath(type, {
      page,
      page_size: 8,
      types: active.join(","),
      q: params.get("log_q") || undefined,
    }),
  );

  const exportCsv = () => {
    if (!data?.items.length) return;
    downloadCsv(
      stampedName(`agent-logs-${type}`),
      toCsv(data.items, [
        { header: "Time", value: (e) => e.time },
        { header: "Call ID", value: (e) => e.call_id },
        { header: "Type", value: (e) => e.type },
        { header: "Event", value: (e) => e.event },
        { header: "Status", value: (e) => e.status },
        { header: "Document ID", value: (e) => e.document_id },
      ]),
    );
  };

  return (
    <>
      <Toolbar
        title="Execution & Decision Log"
        subtitle={
          data
            ? `${data.total} event${data.total === 1 ? "" : "s"} across ${active.length} of ${LOG_KINDS.length} sources`
            : "Execution, gate decisions and routing"
        }
        onExport={exportCsv}
        exportDisabled={!data?.items.length}
        search={q}
        onSearch={setQ}
        searchPlaceholder="Search call ID / event / outcome"
        right={
          <>
            {LOG_KINDS.map((k) => {
              const on = active.includes(k.key);
              return (
                <button
                  key={k.key}
                  className="chip-filter"
                  aria-pressed={on}
                  onClick={() => toggleKind(k.key)}
                >
                  {k.label} {on ? "✕" : ""}
                </button>
              );
            })}
            {active.length < LOG_KINDS.length ? (
              <button
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.delete("log_types");
                  next.set("log_page", "1");
                  setParams(next);
                }}
              >
                Show all
              </button>
            ) : null}
          </>
        }
      />

      <div className="card">
        {loading ? (
          <Loading what="logs" />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !data || data.items.length === 0 ? (
          <Empty
            title="No log events match"
            hint="Try enabling more event types or clearing the search."
          />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col" className="si">
                      Sl
                    </th>
                    <th scope="col">Time</th>
                    <th scope="col">Call ID</th>
                    <th scope="col">Type</th>
                    <th scope="col">Event</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((e, i) => (
                    <tr
                      key={`${e.call_id}-${i}`}
                      className={e.document_id ? "row-link" : undefined}
                      onClick={
                        e.document_id
                          ? () => navigate(`/documents/${e.document_id}`)
                          : undefined
                      }
                    >
                      <td className="si">
                        {(data.page - 1) * data.page_size + i + 1}
                      </td>
                      <td className="mono nowrap">{fmtDateTime(e.time)}</td>
                      <td className="mono nowrap">{e.call_id}</td>
                      <td className="nowrap">
                        <span
                          style={{
                            color:
                              e.type === "Execution"
                                ? "var(--s1)"
                                : e.type === "Decision"
                                  ? "var(--s3)"
                                  : "var(--s2)",
                            fontWeight: 600,
                          }}
                        >
                          {e.type}
                        </span>
                      </td>
                      <td>{e.event}</td>
                      <td>
                        <LogStatus status={e.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.total_pages}
              total={data.total}
              pageSize={data.page_size}
              noun="events"
              onPage={(p) => {
                const next = new URLSearchParams(params);
                next.set("log_page", String(p));
                setParams(next);
              }}
            />
          </>
        )}
      </div>
    </>
  );
}
